/**
 * ChatAIPage — Configuración del motor AI del chat: FAQs, políticas, personalidad, test en vivo.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import {
  Bot, Plus, Trash2, Edit2, Save, X, RefreshCw, Send, BookOpen,
  FileText, Sliders, AlertTriangle, PlayCircle, CheckCircle, Zap,
  Users, UserPlus, Circle, Hand, Truck, MessageCircle, PowerOff,
  Clock, CalendarX, Star, Settings, MessageSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const TABS = [
  { id: 'faqs', label: 'FAQs', icon: BookOpen },
  { id: 'policies', label: 'Políticas', icon: FileText },
  { id: 'instructions', label: 'Instrucciones', icon: Sliders },
  { id: 'welcome', label: 'Bienvenida', icon: Hand },
  { id: 'carriers', label: 'Transportistas', icon: Truck },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'horario', label: 'Horario', icon: Clock },
  { id: 'triggers', label: 'Triggers', icon: Zap },
  { id: 'personality', label: 'Personalidad', icon: Sliders },
  { id: 'limits', label: 'Límites', icon: AlertTriangle },
  { id: 'satisfaction', label: 'Satisfacción', icon: Star },
  { id: 'auto-close', label: 'Conversaciones', icon: Settings },
  { id: 'test', label: 'Test en vivo', icon: PlayCircle },
];

function FAQItem({ item, onSave, onDelete }) {
  const [editing, setEditing] = useState(!item.item_id);
  const [q, setQ] = useState(item.question || '');
  const [a, setA] = useState(item.answer || '');

  const save = async () => {
    if (!a.trim()) return;
    await onSave({ ...item, question: q, answer: a });
    setEditing(false);
  };

  if (!editing) return (
    <div className="flex items-start gap-3 p-3 bg-gray-800/60 rounded-lg border border-gray-700/60 group">
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{item.question || '—'}</p>
        <p className="text-gray-400 text-xs mt-1 leading-relaxed">{item.answer}</p>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setEditing(true)} className="p-1 text-gray-400 hover:text-white">
          <Edit2 size={13} />
        </button>
        <button onClick={() => onDelete(item.item_id)} className="p-1 text-gray-400 hover:text-red-400">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-3 bg-gray-800 rounded-lg border border-indigo-500/40 space-y-2">
      <input
        value={q} onChange={e => setQ(e.target.value)}
        placeholder="Pregunta (ej: ¿Cuánto tarda el envío?)"
        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
      />
      <textarea
        value={a} onChange={e => setA(e.target.value)}
        placeholder="Respuesta..."
        rows={3}
        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500"
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
          <Save size={12} /> Guardar
        </Button>
        {item.item_id && (
          <Button size="sm" variant="outline" onClick={() => setEditing(false)}
            className="border-gray-700 text-gray-400 text-xs">
            Cancelar
          </Button>
        )}
      </div>
    </div>
  );
}

function ChatBubble({ role, content, model_used }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
        isUser
          ? 'bg-indigo-600 text-white rounded-tr-sm'
          : 'bg-gray-800 text-gray-100 rounded-tl-sm border border-gray-700'
      }`}>
        {!isUser && model_used && (
          <p className="text-[10px] text-indigo-400 mb-1">
            {model_used.includes('sonnet') ? 'Claude Sonnet' : 'Claude Haiku'}
          </p>
        )}
        {content}
      </div>
    </div>
  );
}

function TriggerCard({ trigger, idx, onSave, onDelete, onToggle }) {
  const [draft, setDraft] = useState(trigger);
  const [editing, setEditing] = useState(!trigger.trigger_id);

  // Keep draft in sync if parent updates trigger
  useEffect(() => { setDraft(trigger); }, [trigger]);

  const update = (path, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!draft.name?.trim() || !draft.display?.message?.trim()) {
      toast.error('Nombre y mensaje son obligatorios');
      return;
    }
    if ((draft.condition.type === 'time_on_product_page' || draft.condition.type === 'time_on_cart')
        && (!draft.condition.seconds || draft.condition.seconds < 5)) {
      toast.error('Los segundos deben ser ≥ 5');
      return;
    }
    if (draft.condition.type === 'scroll_depth' && (!draft.condition.scroll_percentage || draft.condition.scroll_percentage < 10)) {
      toast.error('El porcentaje de scroll debe ser ≥ 10');
      return;
    }
    await onSave(idx, draft);
    setEditing(false);
  };

  const conditionLabel = {
    time_on_product_page: 'Tiempo en ficha de producto',
    time_on_cart: 'Tiempo en el carrito',
    returning_visitor: 'Visitante recurrente',
    exit_intent: 'Intento de salida (ratón sale arriba)',
    scroll_depth: 'Profundidad de scroll',
    page_contains_keyword: 'Palabra en la página',
    product_brand: 'Marca del producto',
    product_category: 'Categoría del producto',
    product_out_of_stock: 'Producto agotado',
    product_has_discount: 'Producto con descuento',
  }[draft.condition?.type] || draft.condition?.type;

  const formatLabel = {
    bubble_tooltip: 'Bocadillo sobre la burbuja',
    banner: 'Banner dentro del chat',
  }[draft.display?.format] || draft.display?.format;

  if (!editing) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="trigger-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block w-2 h-2 rounded-full ${draft.active ? 'bg-emerald-400' : 'bg-gray-500'}`}></span>
              <h3 className="text-white font-medium text-sm" data-testid="trigger-name">{draft.name}</h3>
            </div>
            <p className="text-gray-400 text-xs mb-2">
              <strong className="text-gray-300">Condición:</strong> {conditionLabel}
              {draft.condition?.seconds ? ` · ${draft.condition.seconds}s` : ''}
              {draft.condition?.scroll_percentage ? ` · ${draft.condition.scroll_percentage}%` : ''}
            </p>
            <p className="text-gray-400 text-xs mb-2">
              <strong className="text-gray-300">Formato:</strong> {formatLabel}
            </p>
            <p className="text-indigo-200 text-sm italic bg-gray-950 border border-gray-800 rounded p-2 mt-1">
              "{draft.display?.message}"
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
              <input
                type="checkbox" checked={!!draft.active}
                onChange={() => onToggle(idx, draft)}
                data-testid="trigger-toggle-active"
                className="w-4 h-4 accent-indigo-500"
              />
              <span className="text-gray-300">Activo</span>
            </label>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="trigger-edit-btn"
              className="h-7 px-2 text-gray-400 hover:text-white">
              <Edit2 size={12} className="mr-1" /> Editar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(idx, draft)} data-testid="trigger-delete-btn"
              className="h-7 px-2 text-red-400 hover:text-red-300">
              <Trash2 size={12} className="mr-1" /> Eliminar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-indigo-500/40 rounded-xl p-4 space-y-3" data-testid="trigger-card-editing">
      <div>
        <label className="block text-gray-400 text-xs mb-1">Nombre interno</label>
        <input
          value={draft.name || ''} onChange={e => update('name', e.target.value)}
          data-testid="trigger-name-input"
          placeholder="Ej: Duda en ficha de producto"
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Condición principal</label>
          <select
            value={draft.condition?.type || 'time_on_product_page'}
            onChange={e => update('condition.type', e.target.value)}
            data-testid="trigger-condition-type"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
          >
            <optgroup label="Tiempo y comportamiento">
              <option value="time_on_product_page">Tiempo en ficha de producto</option>
              <option value="time_on_cart">Tiempo en el carrito</option>
              <option value="returning_visitor">Visitante recurrente</option>
              <option value="exit_intent">Intento de salida</option>
              <option value="scroll_depth">Profundidad de scroll</option>
            </optgroup>
            <optgroup label="Contenido de página">
              <option value="page_contains_keyword">Palabra clave en la página</option>
              <option value="product_brand">Marca del producto</option>
              <option value="product_category">Categoría del producto</option>
              <option value="product_out_of_stock">Producto agotado</option>
              <option value="product_has_discount">Producto con descuento</option>
            </optgroup>
          </select>
        </div>
        {(draft.condition?.type === 'time_on_product_page' || draft.condition?.type === 'time_on_cart') && (
          <div>
            <label className="block text-gray-400 text-xs mb-1">Segundos (5–600)</label>
            <input
              type="number" min="5" max="600"
              value={draft.condition?.seconds || 30}
              onChange={e => update('condition.seconds', parseInt(e.target.value, 10) || 0)}
              data-testid="trigger-condition-seconds"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
            />
          </div>
        )}
        {draft.condition?.type === 'scroll_depth' && (
          <div>
            <label className="block text-gray-400 text-xs mb-1">% de scroll (10–100)</label>
            <input
              type="number" min="10" max="100"
              value={draft.condition?.scroll_percentage || 50}
              onChange={e => update('condition.scroll_percentage', parseInt(e.target.value, 10) || 0)}
              data-testid="trigger-condition-scroll"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
            />
          </div>
        )}
        {['page_contains_keyword', 'product_brand', 'product_category'].includes(draft.condition?.type) && (
          <div>
            <label className="block text-gray-400 text-xs mb-1">
              {draft.condition?.type === 'page_contains_keyword'
                ? 'Palabra/frase a buscar (en título, h1 o meta description)'
                : draft.condition?.type === 'product_brand'
                  ? 'Marca a detectar (ej. Yamaha, Malossi)'
                  : 'Categoría a detectar (ej. cilindros, escapes)'}
            </label>
            <input
              type="text" maxLength={200}
              value={draft.condition?.value || ''}
              onChange={e => update('condition.value', e.target.value)}
              data-testid="trigger-condition-value"
              placeholder="Texto a detectar..."
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* Additional conditions (optional) */}
      <div className="border-t border-gray-800 pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-gray-400 text-xs">Condiciones adicionales (opcional)</label>
          <div className="flex items-center gap-2">
            <select
              value={draft.combinator || 'AND'}
              onChange={e => update('combinator', e.target.value)}
              data-testid="trigger-combinator"
              className="bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-white"
            >
              <option value="AND">Todas (AND)</option>
              <option value="OR">Cualquiera (OR)</option>
            </select>
            <button
              type="button"
              onClick={() => update('additional_conditions', [...(draft.additional_conditions || []), { type: 'product_brand', value: '' }])}
              data-testid="trigger-add-extra-cond"
              className="text-indigo-400 hover:text-indigo-300 text-xs"
            >
              + Añadir condición
            </button>
          </div>
        </div>
        {(draft.additional_conditions || []).map((ec, ei) => (
          <div key={ei} className="flex gap-2 mb-2 items-start">
            <select
              value={ec.type}
              onChange={e => {
                const list = [...(draft.additional_conditions || [])];
                list[ei] = { ...list[ei], type: e.target.value };
                update('additional_conditions', list);
              }}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
            >
              <option value="page_contains_keyword">Palabra clave</option>
              <option value="product_brand">Marca</option>
              <option value="product_category">Categoría</option>
              <option value="product_out_of_stock">Agotado</option>
              <option value="product_has_discount">Con descuento</option>
            </select>
            {['page_contains_keyword', 'product_brand', 'product_category'].includes(ec.type) && (
              <input
                type="text" placeholder="Valor"
                value={ec.value || ''}
                onChange={e => {
                  const list = [...(draft.additional_conditions || [])];
                  list[ei] = { ...list[ei], value: e.target.value };
                  update('additional_conditions', list);
                }}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              />
            )}
            <button
              type="button"
              onClick={() => update('additional_conditions', (draft.additional_conditions || []).filter((_, i) => i !== ei))}
              className="text-red-400 hover:text-red-300 text-xs px-2"
            >×</button>
          </div>
        ))}
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">Formato</label>
        <select
          value={draft.display?.format || 'bubble_tooltip'}
          onChange={e => update('display.format', e.target.value)}
          data-testid="trigger-display-format"
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
        >
          <option value="bubble_tooltip">Bocadillo sobre la burbuja (discreto)</option>
          <option value="banner">Banner dentro del chat</option>
        </select>
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">
          Mensaje · Variables: <code className="text-indigo-300">{'{product_name}'}</code> · <code className="text-indigo-300">{'{visitor_name}'}</code>
        </label>
        <textarea
          rows={2} maxLength={240}
          value={draft.display?.message || ''}
          onChange={e => update('display.message', e.target.value)}
          data-testid="trigger-display-message"
          placeholder="¿Tienes dudas sobre {product_name}?"
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white resize-none focus:border-indigo-500 focus:outline-none"
        />
        <div className="text-right text-gray-500 text-xs mt-0.5">{(draft.display?.message || '').length}/240</div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
        <input
          type="checkbox" checked={!!draft.display?.show_once_per_session}
          onChange={e => update('display.show_once_per_session', e.target.checked)}
          data-testid="trigger-display-once"
          className="w-4 h-4 accent-indigo-500"
        />
        Mostrar solo una vez por sesión
      </label>
      <div className="flex gap-2">
        <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="trigger-save-btn">
          <Save size={13} className="mr-1" /> Guardar
        </Button>
        <Button variant="ghost" onClick={() => { setDraft(trigger); setEditing(false); if (!trigger.trigger_id) onDelete(idx, trigger); }}
          className="text-gray-400" data-testid="trigger-cancel-btn">
          <X size={13} className="mr-1" /> Cancelar
        </Button>
      </div>
    </div>
  );
}

// ── Business Hours Tab ─────────────────────────────────────────

const DAYS_ES = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
};
const DAYS_ORDER = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DEFAULT_SLOTS = [{ from: '09:00', to: '13:00' }, { from: '15:00', to: '18:00' }];
const DEFAULT_SCHEDULE = DAYS_ORDER.reduce((acc, d) => ({
  ...acc,
  [d]: {
    enabled: ['monday','tuesday','wednesday','thursday','friday'].includes(d),
    slots: ['monday','tuesday','wednesday','thursday','friday'].includes(d) ? [...DEFAULT_SLOTS] : [],
  }
}), {});

function TimeSlot({ slot, onChange, onRemove, canRemove }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="time" value={slot.from}
        onChange={e => onChange({ ...slot, from: e.target.value })}
        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-indigo-500 w-[110px]"
      />
      <span className="text-gray-500 text-xs">–</span>
      <input
        type="time" value={slot.to}
        onChange={e => onChange({ ...slot, to: e.target.value })}
        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-indigo-500 w-[110px]"
      />
      {canRemove && (
        <button onClick={onRemove} className="text-gray-600 hover:text-red-400 transition-colors p-1">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function DayRow({ dayKey, dayLabel, config, onChange }) {
  const updateSlot = (i, slot) => {
    const slots = [...config.slots];
    slots[i] = slot;
    onChange({ ...config, slots });
  };
  const removeSlot = (i) => {
    onChange({ ...config, slots: config.slots.filter((_, idx) => idx !== i) });
  };
  const addSlot = () => {
    const last = config.slots[config.slots.length - 1];
    const newFrom = last ? last.to : '09:00';
    onChange({ ...config, slots: [...config.slots, { from: newFrom, to: '18:00' }] });
  };

  return (
    <div className={`p-3 rounded-lg border transition-colors ${
      config.enabled ? 'bg-gray-800/60 border-gray-700/60' : 'bg-gray-900/40 border-gray-800/40 opacity-60'
    }`}>
      <div className="flex items-center gap-3 mb-2">
        <Switch
          checked={config.enabled}
          onCheckedChange={v => onChange({ ...config, enabled: v, slots: v && config.slots.length === 0 ? [{ from: '09:00', to: '18:00' }] : config.slots })}
          data-testid={`day-toggle-${dayKey}`}
        />
        <span className={`text-sm font-medium w-24 ${config.enabled ? 'text-white' : 'text-gray-500'}`}>{dayLabel}</span>
        {!config.enabled && <span className="text-gray-600 text-xs italic">Cerrado</span>}
        {config.enabled && config.slots.length === 0 && (
          <span className="text-amber-500 text-xs">Sin franjas — añade al menos una</span>
        )}
      </div>
      {config.enabled && (
        <div className="ml-12 space-y-1.5">
          {config.slots.map((slot, i) => (
            <TimeSlot
              key={i} slot={slot}
              onChange={s => updateSlot(i, s)}
              onRemove={() => removeSlot(i)}
              canRemove={config.slots.length > 1}
            />
          ))}
          <button
            onClick={addSlot}
            className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1 mt-1"
          >
            <Plus size={11} /> Añadir franja
          </button>
        </div>
      )}
    </div>
  );
}

function BusinessHoursTab() {
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [holidays, setHolidays] = useState([]);
  const [outsideMsg, setOutsideMsg] = useState('');
  const [newHoliday, setNewHoliday] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOpenNow, setIsOpenNow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/chat/business-hours');
      const sched = r.data.schedule || DEFAULT_SCHEDULE;
      // Ensure all 7 days are present
      const full = { ...DEFAULT_SCHEDULE, ...sched };
      setSchedule(full);
      setHolidays(r.data.holidays || []);
      setOutsideMsg(r.data.outside_hours_message || '');
    } catch { toast.error('Error al cargar horario'); }
    finally { setLoading(false); }
  }, []);

  // Check is-open status
  const checkOpen = useCallback(async () => {
    try {
      const r = await api.get('/api/chat/is-open');
      setIsOpenNow(r.data.is_open);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); checkOpen(); }, [load, checkOpen]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/chat/business-hours', {
        schedule,
        holidays,
        outside_hours_message: outsideMsg,
      });
      toast.success('Horario guardado');
      await checkOpen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const addHoliday = () => {
    if (!newHoliday || holidays.includes(newHoliday)) return;
    setHolidays(h => [...h, newHoliday].sort());
    setNewHoliday('');
  };

  const removeHoliday = (d) => setHolidays(h => h.filter(x => x !== d));

  const formatHolidayLabel = (iso) => {
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    } catch { return iso; }
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <RefreshCw size={18} className="animate-spin text-indigo-400" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="business-hours-tab">
      {/* Status badge */}
      <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium ${
        isOpenNow === true
          ? 'bg-green-500/10 border-green-500/30 text-green-300'
          : isOpenNow === false
          ? 'bg-red-500/10 border-red-500/30 text-red-300'
          : 'bg-gray-800 border-gray-700 text-gray-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${
          isOpenNow === true ? 'bg-green-400 animate-pulse' : isOpenNow === false ? 'bg-red-400' : 'bg-gray-500'
        }`} />
        {isOpenNow === true ? 'Abierto ahora' : isOpenNow === false ? 'Cerrado ahora' : 'Comprobando...'}
        <span className="ml-auto text-xs opacity-60 font-normal">Zona horaria: Europe/Madrid</span>
      </div>

      {/* Info */}
      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
        <p className="text-indigo-300 text-xs leading-relaxed">
          <strong>Horario de atención.</strong> Define cuándo están disponibles tus agentes.
          Fuera de este horario, el bot seguirá respondiendo consultas generales pero avisará
          al cliente de que no hay agentes humanos disponibles y no ofrecerá la opción de escalar.
        </p>
      </div>

      {/* Days */}
      <div>
        <h3 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
          <Clock size={14} className="text-indigo-400" /> Días y franjas horarias
        </h3>
        <div className="space-y-2">
          {DAYS_ORDER.map(d => (
            <DayRow
              key={d} dayKey={d} dayLabel={DAYS_ES[d]}
              config={schedule[d] || { enabled: false, slots: [] }}
              onChange={cfg => setSchedule(s => ({ ...s, [d]: cfg }))}
            />
          ))}
        </div>
      </div>

      {/* Holidays */}
      <div>
        <h3 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
          <CalendarX size={14} className="text-indigo-400" /> Días festivos / excepciones
        </h3>
        <p className="text-gray-500 text-xs mb-3">Añade los días que permanecéis cerrados aunque sea un día laborable (festivos nacionales, locales, vacaciones…).</p>
        <div className="flex gap-2 mb-3">
          <input
            type="date" value={newHoliday}
            onChange={e => setNewHoliday(e.target.value)}
            data-testid="add-holiday-input"
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
          />
          <Button size="sm" onClick={addHoliday} disabled={!newHoliday}
            data-testid="add-holiday-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1">
            <Plus size={12} /> Añadir
          </Button>
        </div>
        {holidays.length === 0 ? (
          <p className="text-gray-600 text-xs italic">Sin festivos configurados.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {holidays.map(d => (
              <span key={d}
                data-testid={`holiday-chip-${d}`}
                className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                {formatHolidayLabel(d)} <span className="text-gray-600 text-[10px]">({d})</span>
                <button onClick={() => removeHoliday(d)} className="text-gray-600 hover:text-red-400 ml-1">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Outside hours message */}
      <div>
        <label className="block text-white text-sm font-medium mb-1">
          Mensaje fuera de horario
        </label>
        <p className="text-gray-500 text-xs mb-2">
          El bot incluirá este mensaje cuando un cliente escriba fuera del horario configurado.
        </p>
        <textarea
          value={outsideMsg}
          onChange={e => setOutsideMsg(e.target.value)}
          data-testid="outside-hours-message-input"
          rows={3} maxLength={400}
          placeholder="En este momento nuestro equipo no está disponible..."
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 resize-none focus:outline-none focus:border-indigo-500"
        />
        <div className="text-right text-gray-600 text-xs mt-0.5">{outsideMsg.length}/400</div>
      </div>

      <Button onClick={save} disabled={saving}
        data-testid="save-business-hours-btn"
        className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
        {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
        Guardar horario
      </Button>
    </div>
  );
}




// ── Welcome Cards Tab ─────────────────────────────────────────────
function WelcomeCardEditor({ card, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState({
    title: card.title || '',
    action_type: card.action_type || 'send_message',
    message_to_send: card.message_to_send || '',
    response_text: card.response_text || '',
    external_url: card.external_url || '',
    icon: card.icon || '',
    visible_logged_in: card.visible_logged_in !== false,
    visible_logged_out: card.visible_logged_out !== false,
    order: card.order || 0,
    active: card.active !== false,
  });

  const update = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="p-4 bg-gray-800 rounded-lg border border-indigo-500/40 space-y-3">
      <div>
        <label className="block text-gray-400 text-xs mb-1">Título del botón</label>
        <input
          value={draft.title}
          onChange={e => update('title', e.target.value)}
          data-testid="welcome-card-title-input"
          maxLength={80}
          placeholder="Ej: ¿Dónde está mi pedido?"
          className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
        />
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">Acción al clicar</label>
        <select
          value={draft.action_type}
          onChange={e => update('action_type', e.target.value)}
          data-testid="welcome-card-action-select"
          className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="send_message">Enviar mensaje al chat (asistente responde con IA)</option>
          <option value="hybrid">Mostrar respuesta inmediata (texto + enlace)</option>
          <option value="external_link">Abrir enlace externo</option>
        </select>
      </div>
      {draft.action_type === 'send_message' && (
        <div>
          <label className="block text-gray-400 text-xs mb-1">Mensaje que se enviará al chat</label>
          <input
            value={draft.message_to_send}
            onChange={e => update('message_to_send', e.target.value)}
            placeholder="Ej: Quiero saber el estado de mi pedido"
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
          />
        </div>
      )}
      {draft.action_type === 'hybrid' && (
        <>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Respuesta inmediata del bot</label>
            <textarea
              value={draft.response_text}
              onChange={e => update('response_text', e.target.value)}
              rows={3}
              placeholder="Para devolver un producto, sigue las instrucciones en nuestro portal..."
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">URL del enlace (opcional)</label>
            <input
              value={draft.external_url}
              onChange={e => update('external_url', e.target.value)}
              placeholder="https://devoluciones.tutienda.com"
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
            />
          </div>
        </>
      )}
      {draft.action_type === 'external_link' && (
        <div>
          <label className="block text-gray-400 text-xs mb-1">URL externa</label>
          <input
            value={draft.external_url}
            onChange={e => update('external_url', e.target.value)}
            placeholder="https://devoluciones.tutienda.com"
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={draft.visible_logged_out}
            onChange={e => update('visible_logged_out', e.target.checked)} />
          Visible si NO está logueado
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={draft.visible_logged_in}
            onChange={e => update('visible_logged_in', e.target.checked)} />
          Visible si SÍ está logueado
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Orden</label>
          <input type="number" value={draft.order}
            onChange={e => update('order', parseInt(e.target.value || '0', 10))}
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
        </div>
        <label className="flex items-end gap-2 text-xs text-gray-400 pb-1">
          <input type="checkbox" checked={draft.active}
            onChange={e => update('active', e.target.checked)} />
          Activa
        </label>
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" onClick={() => onSave(draft)}
          data-testid="welcome-card-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
          <Save size={12} /> Guardar
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}
          className="border-gray-700 text-gray-400 hover:text-white">
          Cancelar
        </Button>
        {card.card_id && (
          <Button size="sm" variant="outline" onClick={() => onDelete(card.card_id)}
            data-testid="welcome-card-delete-btn"
            className="ml-auto border-red-900/50 text-red-400 hover:bg-red-950">
            <Trash2 size={12} /> Eliminar
          </Button>
        )}
      </div>
    </div>
  );
}

function WelcomeCardsTab() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/chat/welcome-cards');
      setCards(r.data.items || []);
    } catch { toast.error('Error al cargar tarjetas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCard = async (cardOrId, draft) => {
    try {
      if (cardOrId && cardOrId !== '__new__') {
        await api.put(`/api/chat/welcome-cards/${cardOrId}`, draft);
        toast.success('Tarjeta actualizada');
      } else {
        await api.post('/api/chat/welcome-cards', draft);
        toast.success('Tarjeta creada');
      }
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('¿Eliminar esta tarjeta?')) return;
    try {
      await api.delete(`/api/chat/welcome-cards/${cardId}`);
      setEditingId(null);
      await load();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
        <p className="text-indigo-300 text-xs leading-relaxed">
          <strong>Tarjetas de bienvenida.</strong> Aparecen junto al primer mensaje del chat como
          botones rápidos. Útil para "Consulta técnica", "¿Dónde está mi pedido?", "Devoluciones",
          etc. Puedes mostrar tarjetas diferentes según el cliente esté logueado o no.
        </p>
      </div>
      <Button size="sm" onClick={() => setEditingId('__new__')}
        data-testid="add-welcome-card-btn"
        className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1">
        <Plus size={12} /> Nueva tarjeta
      </Button>

      {editingId === '__new__' && (
        <WelcomeCardEditor
          card={{}}
          onSave={(d) => saveCard(null, d)}
          onCancel={() => setEditingId(null)}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw size={16} className="animate-spin text-gray-500" />
        </div>
      ) : cards.length === 0 && editingId !== '__new__' ? (
        <div className="text-center py-10 text-gray-600 border border-dashed border-gray-800 rounded-lg">
          <Hand size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aún no has creado tarjetas de bienvenida</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cards.map(c => (
            editingId === c.card_id ? (
              <WelcomeCardEditor key={c.card_id} card={c}
                onSave={(d) => saveCard(c.card_id, d)}
                onDelete={deleteCard}
                onCancel={() => setEditingId(null)} />
            ) : (
              <div key={c.card_id}
                data-testid={`welcome-card-row-${c.card_id}`}
                className="flex items-center gap-3 p-3 bg-gray-800/60 rounded-lg border border-gray-700/60 group">
                <span className={`w-2 h-2 rounded-full ${c.active ? 'bg-green-400' : 'bg-gray-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{c.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {c.action_type === 'send_message' && `Envía: "${c.message_to_send || '—'}"`}
                    {c.action_type === 'hybrid' && (c.response_text || c.external_url || '—')}
                    {c.action_type === 'external_link' && c.external_url}
                  </p>
                </div>
                <button onClick={() => setEditingId(c.card_id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-white">
                  <Edit2 size={13} />
                </button>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// ── Carriers Tab ──────────────────────────────────────────────────
function CarrierRuleEditor({ rule, onSave, onCancel }) {
  const [draft, setDraft] = useState({
    carrier_name: rule.carrier_name || '',
    regex_pattern: rule.regex_pattern || '',
    tracking_url_template: rule.tracking_url_template || '',
    priority: rule.priority || 100,
    active: rule.active !== false,
  });
  const [error, setError] = useState(null);
  const update = (k, v) => { setError(null); setDraft(d => ({ ...d, [k]: v })); };

  // Validación client-side: detecta los errores comunes antes de mandar al backend.
  const clientValidate = () => {
    if (!draft.carrier_name.trim()) return 'El nombre del transportista no puede estar vacío.';
    if (draft.carrier_name.length > 60) return 'El nombre no puede tener más de 60 caracteres.';
    if (!draft.regex_pattern.trim()) return 'El patrón regex no puede estar vacío.';
    if (draft.regex_pattern.length > 200) return 'El regex es demasiado largo (máx 200 caracteres).';
    try {
      // eslint-disable-next-line no-new
      new RegExp(draft.regex_pattern);
    } catch (e) {
      return `El regex tiene un error de sintaxis: ${e.message}`;
    }
    const url = draft.tracking_url_template.trim();
    if (!url) return 'La URL de seguimiento no puede estar vacía.';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return 'La URL debe empezar por http:// o https://';
    }
    return null;
  };

  const handleSave = () => {
    const err = clientValidate();
    if (err) { setError(err); return; }
    onSave(draft);
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg border border-indigo-500/40 space-y-3">
      <div>
        <label className="block text-gray-400 text-xs mb-1">Nombre del transportista</label>
        <input value={draft.carrier_name} onChange={e => update('carrier_name', e.target.value)}
          placeholder="Ej: Mi Transportista"
          data-testid="carrier-name-input"
          className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">
          Patrón del nº de seguimiento (regex)
        </label>
        <p className="text-gray-500 text-[11px] mb-1 leading-relaxed">
          Ejemplos simples: <code>{'^\\d{14}$'}</code> (14 dígitos) ·
          <code>{'^[A-Z]{2}\\d{9}[A-Z]{2}$'}</code> (Correos).<br />
          <strong className="text-indigo-300">Grupos nombrados</strong>: si el tracking
          tiene partes (ej. Nacex <code>1711/11480132</code> = agencia + albarán),
          usa <code>{'(?P<nombre>...)'}</code> y refer&eacute;ncialos en la URL como{' '}
          <code>{'{nombre}'}</code>.<br />
          Ej. <code>{'^(?P<agencia>\\d{4})/(?P<albaran>\\d+)$'}</code>
        </p>
        <input value={draft.regex_pattern} onChange={e => update('regex_pattern', e.target.value)}
          placeholder="^\d{14}$  ó  ^(?P<agencia>\d{4})/(?P<albaran>\d+)$"
          data-testid="carrier-regex-input"
          className="w-full font-mono bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
      </div>
      <div>
        <label className="block text-gray-400 text-xs mb-1">
          URL de seguimiento
        </label>
        <p className="text-gray-500 text-[11px] mb-1 leading-relaxed">
          Opcional: usa <code className="text-indigo-300">{'{tracking}'}</code> para insertar
          el número completo, o <code className="text-indigo-300">{'{nombre_de_grupo}'}</code>{' '}
          para cada grupo del regex.<br />
          Ej. Nacex: <code>{'https://www.nacex.es/seguimientoDetalle.do?agencia_origen={agencia}&numero_albaran={albaran}'}</code>
        </p>
        <input value={draft.tracking_url_template} onChange={e => update('tracking_url_template', e.target.value)}
          placeholder="https://midomain.com/track?n={tracking}"
          data-testid="carrier-url-input"
          className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Prioridad (menor = primero)</label>
          <input type="number" value={draft.priority} onChange={e => update('priority', parseInt(e.target.value || '100', 10))}
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
        </div>
        <label className="flex items-end gap-2 text-xs text-gray-400 pb-1">
          <input type="checkbox" checked={draft.active} onChange={e => update('active', e.target.checked)} />
          Activa
        </label>
      </div>
      {error && (
        <div data-testid="carrier-form-error"
          className="p-2.5 bg-red-950/40 border border-red-500/40 rounded text-red-300 text-xs leading-relaxed">
          ⚠️ {error}
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <Button size="sm" onClick={handleSave}
          data-testid="carrier-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"><Save size={12} /> Guardar</Button>
        <Button size="sm" variant="outline" onClick={onCancel}
          className="border-gray-700 text-gray-400 hover:text-white">Cancelar</Button>
      </div>
    </div>
  );
}

function CarriersTab() {
  const [customRules, setCustomRules] = useState([]);
  const [defaultRules, setDefaultRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [testNum, setTestNum] = useState('');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/chat/carrier-rules');
      setCustomRules(r.data.custom || []);
      setDefaultRules(r.data.defaults || []);
    } catch { toast.error('Error al cargar reglas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRule = async (ruleIdOrNew, draft) => {
    try {
      if (ruleIdOrNew && ruleIdOrNew !== '__new__') {
        await api.put(`/api/chat/carrier-rules/${ruleIdOrNew}`, draft);
        toast.success('Regla actualizada');
      } else {
        await api.post('/api/chat/carrier-rules', draft);
        toast.success('Regla creada');
      }
      setEditingId(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
  };

  const deleteRule = async (rule_id) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await api.delete(`/api/chat/carrier-rules/${rule_id}`);
      await load();
    } catch { toast.error('Error al eliminar'); }
  };

  const testDetect = async () => {
    if (!testNum.trim()) return;
    try {
      const r = await api.post('/api/chat/carrier-rules/test', { tracking_number: testNum.trim() });
      setTestResult(r.data.detected);
    } catch { toast.error('Error en el test'); }
  };

  const toggleDefault = async (carrier_name, currentlyDisabled) => {
    try {
      await api.post('/api/chat/carrier-rules/defaults/toggle', {
        carrier_name,
        disabled: !currentlyDisabled,
      });
      toast.success(currentlyDisabled ? 'Carrier activado' : 'Carrier desactivado');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
        <p className="text-indigo-300 text-xs leading-relaxed">
          <strong>Detección automática de transportista.</strong> El chat usa el formato del número
          de seguimiento (regex) para deducir qué empresa de transporte gestiona el envío y
          generar el enlace de tracking. Tus reglas <strong>tienen prioridad</strong> sobre las
          reglas por defecto del sistema.
        </p>
      </div>

      {/* Test panel */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 space-y-2">
        <p className="text-gray-300 text-xs font-medium">Probar detección</p>
        <div className="flex gap-2">
          <input value={testNum} onChange={e => setTestNum(e.target.value)}
            placeholder="Pega un nº de seguimiento real"
            data-testid="carrier-test-input"
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
          <Button size="sm" onClick={testDetect}
            data-testid="carrier-test-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white">Detectar</Button>
        </div>
        {testResult !== null && (
          <div className="text-xs">
            {testResult ? (
              <p className="text-green-400" data-testid="carrier-test-result">
                ✓ <strong>{testResult.carrier_name}</strong> — <a className="text-indigo-400 underline" href={testResult.tracking_url} target="_blank" rel="noopener noreferrer">{testResult.tracking_url}</a>
              </p>
            ) : (
              <p className="text-amber-400">✗ Sin coincidencia. Crea una regla custom abajo.</p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-white text-sm font-medium">Tus reglas custom</h3>
        <Button size="sm" onClick={() => setEditingId('__new__')}
          data-testid="add-carrier-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1">
          <Plus size={12} /> Nueva regla
        </Button>
      </div>

      {editingId === '__new__' && (
        <CarrierRuleEditor rule={{}} onSave={(d) => saveRule(null, d)} onCancel={() => setEditingId(null)} />
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <RefreshCw size={16} className="animate-spin text-gray-500" />
        </div>
      ) : customRules.length === 0 && editingId !== '__new__' ? (
        <p className="text-gray-600 text-xs italic">Sin reglas custom. Se usan las del sistema (abajo).</p>
      ) : (
        <div className="space-y-2">
          {customRules.map(r => (
            editingId === r.rule_id ? (
              <CarrierRuleEditor key={r.rule_id} rule={r}
                onSave={(d) => saveRule(r.rule_id, d)}
                onCancel={() => setEditingId(null)} />
            ) : (
              <div key={r.rule_id}
                data-testid={`carrier-row-${r.rule_id}`}
                className="flex items-center gap-3 p-3 bg-gray-800/60 rounded-lg border border-gray-700/60 group">
                <span className={`w-2 h-2 rounded-full ${r.active ? 'bg-green-400' : 'bg-gray-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{r.carrier_name} <span className="text-gray-500 text-xs">· prioridad {r.priority}</span></p>
                  <p className="text-gray-500 text-xs font-mono mt-0.5 truncate">{r.regex_pattern}</p>
                </div>
                <button onClick={() => setEditingId(r.rule_id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-white">
                  <Edit2 size={13} />
                </button>
                <button onClick={() => deleteRule(r.rule_id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-400">
                  <Trash2 size={13} />
                </button>
              </div>
            )
          ))}
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-gray-400 text-sm font-medium">Reglas por defecto (sistema)</h3>
        </div>
        <p className="text-gray-600 text-xs mb-3">
          Sólo se aplican si ninguna de tus reglas custom coincide. Desactiva los carriers
          que NO uses en tu tienda para evitar falsos positivos (ej: si nunca usas SEUR,
          los tracking de 14 dígitos se asignarán al siguiente carrier que coincida).
        </p>
        <div className="space-y-1">
          {defaultRules.map((r) => (
            <div key={r.carrier_name}
              data-testid={`default-carrier-row-${r.carrier_name}`}
              className={`flex items-center gap-3 px-3 py-2 rounded text-xs transition-opacity ${r.disabled ? 'opacity-40' : ''} bg-gray-900/40`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!r.disabled}
                  onChange={() => toggleDefault(r.carrier_name, r.disabled)}
                  data-testid={`default-carrier-toggle-${r.carrier_name}`}
                  className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                />
              </label>
              <span className="text-gray-300 w-32 font-medium">{r.carrier_name}</span>
              <span className="text-gray-600 font-mono truncate flex-1">{r.regex_pattern}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── WhatsApp Tab ──────────────────────────────────────────────
function WhatsAppTab() {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [configured, setConfigured] = useState(false);
  // Config data
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneName, setPhoneName] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [tokenHint, setTokenHint] = useState('');
  // Meta Embedded Signup (opcional si hay config_id)
  const [metaAppId, setMetaAppId] = useState('');
  const [metaConfigId, setMetaConfigId] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  // Multi-phone selection
  const [pendingPhones, setPendingPhones] = useState(null);
  const [pendingToken, setPendingToken] = useState('');
  const [pendingIsTmpKey, setPendingIsTmpKey] = useState(false);
  // Token connect (flujo principal)
  const [quickToken, setQuickToken] = useState('');
  const [quickWabaId, setQuickWabaId] = useState('');
  const [showWabaHelp, setShowWabaHelp] = useState(false);
  // Confirmación antes de vincular
  const [preview, setPreview] = useState(null); // { phone_number_id, phone_number, phone_name, waba_id, access_token }
  // Manual form toggle
  const [showManual, setShowManual] = useState(false);
  const [manualPhoneId, setManualPhoneId] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/whatsapp/config');
      setConfigured(r.data.configured || false);
      setWebhookUrl(r.data.webhook_url || '');
      setVerifyToken(r.data.verify_token || '');
      setMetaAppId(r.data.meta_app_id || '');
      setMetaConfigId(r.data.meta_config_id || '');
      if (r.data.config) {
        setPhoneNumberId(r.data.config.phone_number_id || '');
        setPhoneNumber(r.data.config.phone_number || '');
        setPhoneName(r.data.config.phone_name || '');
        setWabaId(r.data.config.waba_id || '');
        setTokenHint(r.data.config.access_token_hint || '****');
      }
    } catch {
      toast.error('Error al cargar configuración de WhatsApp');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Cargar el Facebook JS SDK ────────────────────────────────
  useEffect(() => {
    if (!metaAppId) return;
    if (window.FB) { setSdkReady(true); return; }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: metaAppId,
        cookie: true,
        xfbml: true,
        version: 'v22.0',
      });
      setSdkReady(true);
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [metaAppId]);

  // ── Lanzar OAuth popup (estándar, sin BSP) ──────────────────
  const launchOAuth = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/api/whatsapp/oauth/url');
      const oauthUrl = res.data.url;

      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(oauthUrl, 'sera_wa_oauth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`);

      if (!popup) {
        toast.error('El navegador bloqueó el popup. Permite popups para este sitio e inténtalo de nuevo.');
        setConnecting(false);
        return;
      }

      const handleMessage = async (event) => {
        if (!event.data || event.data.type !== 'SERA_WA_OAUTH') return;
        window.removeEventListener('message', handleMessage);
        setConnecting(false);

        const d = event.data;
        if (!d.success) {
          toast.error(d.message || 'Error en la conexión con Meta');
          return;
        }
        if (d.status === 'select_phone') {
          // Múltiples números — mostrar selector (cada número lleva su propia WABA)
          setPendingPhones(d.phones);
          setPendingToken(d.tmp_key);   // reutilizamos el estado para guardar tmp_key
          setPendingIsTmpKey(true);
        } else {
          toast.success(`WhatsApp conectado: ${d.phone_number || d.phone_number_id}`);
          await load();
        }
      };
      window.addEventListener('message', handleMessage);

      // Fallback: si el popup se cierra sin postMessage
      const pollClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollClosed);
          window.removeEventListener('message', handleMessage);
          setConnecting(false);
        }
      }, 800);
    } catch (e) {
      toast.error('Error al iniciar conexión: ' + (e?.response?.data?.detail || e.message || ''));
      setConnecting(false);
    }
  };

  // ── Confirmar selección de número (multi-número) ─────────────
  const selectPhone = async (phone) => {
    setSaving(true);
    try {
      if (pendingIsTmpKey) {
        // Flujo OAuth — usar tmp_key
        await api.post('/api/whatsapp/oauth/select-phone', {
          tmp_key: pendingToken,
          phone_number_id: phone.id,
        });
      } else {
        // Flujo token-connect
        await api.post('/api/whatsapp/embedded-signup/select-phone', {
          phone_number_id: phone.id,
          phone_number: phone.display_phone_number,
          phone_name: phone.verified_name,
          waba_id: phone.waba_id,
          access_token: pendingToken,
        });
      }
      toast.success(`WhatsApp conectado: ${phone.display_phone_number}`);
      setPendingPhones(null);
      setPendingToken('');
      setPendingIsTmpKey(false);
      await load();
    } catch (e) {
      toast.error('Error al guardar el número: ' + (e?.response?.data?.detail || e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  // ── Paso 1: Detectar (dry_run=true) — NO guarda nada ────────
  const detectToken = async () => {
    if (!quickToken.trim()) { toast.error('Pega tu token de acceso'); return; }
    setConnecting(true);
    try {
      const payload = { access_token: quickToken.trim(), dry_run: true };
      if (quickWabaId.trim()) payload.waba_id = quickWabaId.trim();
      const res = await api.post('/api/whatsapp/token-connect', payload);
      if (res.data.status === 'preview') {
        setPreview({ ...res.data, access_token: quickToken.trim() });
      } else if (res.data.status === 'select_phone') {
        setPendingPhones(res.data.phones);
        setPendingToken(res.data.access_token || quickToken.trim());
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || '';
      toast.error(msg, { duration: 8000 });
      if (msg.toLowerCase().includes('waba')) setShowWabaHelp(true);
    } finally {
      setConnecting(false);
    }
  };

  // ── Paso 2: Confirmar y guardar ──────────────────────────────
  const confirmConnect = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = { access_token: preview.access_token, dry_run: false };
      if (preview.waba_id) payload.waba_id = preview.waba_id;
      const res = await api.post('/api/whatsapp/token-connect', payload);
      if (res.data.status === 'connected') {
        toast.success(`WhatsApp conectado: ${res.data.phone_number || res.data.phone_number_id}`);
        setPreview(null);
        setQuickToken('');
        setQuickWabaId('');
        await load();
      }
    } catch (e) {
      toast.error('Error al conectar: ' + (e?.response?.data?.detail || e.message || ''));
    } finally {
      setSaving(false);
    }
  };
  const saveManual = async () => {
    if (!manualPhoneId.trim()) { toast.error('El Phone Number ID es obligatorio'); return; }
    if (!configured && !manualToken.trim()) { toast.error('El Access Token es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = { phone_number_id: manualPhoneId.trim(), phone_number: manualPhone.trim() };
      if (manualToken.trim()) payload.access_token = manualToken.trim();
      await api.post('/api/whatsapp/config', payload);
      toast.success('Configuración guardada');
      setManualToken('');
      setShowManual(false);
      await load();
    } catch (e) {
      toast.error('Error: ' + (e?.response?.data?.detail || e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  // ── Desconectar ──────────────────────────────────────────────
  const remove = async () => {
    if (!window.confirm('¿Desconectar WhatsApp? Se eliminarán el token y la configuración del número.')) return;
    setDeleting(true);
    try {
      await api.delete('/api/whatsapp/config');
      toast.success('WhatsApp desconectado');
      setConfigured(false);
      setPhoneNumberId(''); setPhoneNumber(''); setPhoneName(''); setWabaId(''); setTokenHint('');
    } catch { toast.error('Error al desconectar'); }
    finally { setDeleting(false); }
  };

  // ── Re-suscribir WABA al webhook ─────────────────────────────
  const [resubscribing, setResubscribing] = useState(false);
  const [resubResult, setResubResult] = useState(null);
  const resubscribe = async () => {
    setResubscribing(true);
    try {
      const res = await api.post('/api/whatsapp/resubscribe');
      setResubResult(res.data);
      if (res.data.persisted) toast.success(res.data.message);
      else toast.error(res.data.message, { duration: 10000 });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || '';
      setResubResult({ error: detail });
      toast.error('Error: ' + detail);
    } finally {
      setResubscribing(false);
    }
  };

  // ── Registrar número en la Cloud API (paso distinto de vincularlo a la WABA) ──
  const [registering, setRegistering] = useState(false);
  const [registerPin, setRegisterPin] = useState('');
  const [registerResult, setRegisterResult] = useState(null);
  const registerNumber = async () => {
    setRegistering(true);
    try {
      const payload = registerPin.trim() ? { pin: registerPin.trim() } : {};
      const res = await api.post('/api/whatsapp/register-number', payload);
      setRegisterResult(res.data);
      if (res.data.registered) {
        toast.success('Número registrado en la Cloud API. Los mensajes reales deberían empezar a llegar.');
        checkSubscription();
      } else {
        toast.error(res.data.error || 'No se pudo registrar el número', { duration: 10000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || '';
      setRegisterResult({ error: detail });
      toast.error('Error: ' + detail);
    } finally {
      setRegistering(false);
    }
  };

  // ── Test diagnóstico ──────────────────────────────────────────
  const [testing, setTesting] = useState(false);
  const runTestMessage = async () => {
    setTesting(true);
    try {
      const res = await api.post('/api/whatsapp/test-incoming');
      toast.success(`Pipeline OK — ${res.data.message}`, { duration: 6000 });
    } catch (e) {
      toast.error('Error en test: ' + (e?.response?.data?.detail || e.message || ''));
    } finally {
      setTesting(false);
    }
  };

  // ── Log de webhooks recibidos ─────────────────────────────────
  const [webhookLog, setWebhookLog] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const loadWebhookLog = async () => {
    setLoadingLog(true);
    try {
      const res = await api.get('/api/whatsapp/webhook-log');
      setWebhookLog(res.data.logs || []);
    } catch {
      setWebhookLog([]);
    } finally {
      setLoadingLog(false);
    }
  };

  // ── Check suscripción Meta ────────────────────────────────────
  const [subInfo, setSubInfo] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const checkSubscription = async () => {
    setLoadingSub(true);
    try {
      const res = await api.get('/api/whatsapp/check-subscription');
      setSubInfo(res.data);
    } catch (e) {
      setSubInfo({ error: e?.response?.data?.detail || e.message || 'Error' });
    } finally {
      setLoadingSub(false);
    }
  };

  // ── Check webhook real configurado en la app de Meta ─────────
  const [appWh, setAppWh] = useState(null);
  const [loadingAppWh, setLoadingAppWh] = useState(false);
  const checkAppWebhook = async () => {
    setLoadingAppWh(true);
    try {
      const res = await api.get('/api/whatsapp/check-app-webhook');
      setAppWh(res.data);
    } catch (e) {
      setAppWh({ error: e?.response?.data?.detail || e.message || 'Error' });
    } finally {
      setLoadingAppWh(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copiado'));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <RefreshCw size={18} className="animate-spin text-indigo-400" />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── Estado actual ─────────────────────────────────────── */}
      {configured ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={18} className="text-green-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-green-300 text-sm font-semibold">
                    {phoneName || phoneNumber || phoneNumberId}
                  </p>
                  <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded-full">
                    <CheckCircle size={9} /> Conectado
                  </span>
                </div>
                <p className="text-green-400/60 text-xs mt-0.5">
                  {phoneNumber && phoneNumber !== phoneName ? phoneNumber : ''}
                  {wabaId ? ` · WABA ${wabaId}` : ''}
                </p>
                <p className="text-green-400/40 text-xs mt-0.5 font-mono">Token: {tokenHint}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <Button
                data-testid="wa-resubscribe-btn"
                variant="outline"
                size="sm"
                onClick={resubscribe}
                disabled={resubscribing}
                className="border-indigo-700 text-indigo-400 hover:text-indigo-300 hover:border-indigo-500 gap-1.5">
                {resubscribing ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Re-suscribir
              </Button>
              <Button
                data-testid="wa-register-number-btn"
                variant="outline"
                size="sm"
                onClick={registerNumber}
                disabled={registering}
                className="border-amber-700 text-amber-400 hover:text-amber-300 hover:border-amber-500 gap-1.5">
                {registering ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                Registrar número
              </Button>
              <Button
                data-testid="wa-test-btn"
                variant="outline"
                size="sm"
                onClick={runTestMessage}
                disabled={testing}
                className="border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500 gap-1.5">
                {testing ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                Test pipeline
              </Button>
              <Button
                data-testid="wa-delete-btn"
                variant="outline"
                size="sm"
                onClick={remove}
                disabled={deleting}
                className="border-red-800 text-red-400 hover:text-red-300 hover:border-red-600 gap-1.5">
                {deleting ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Desconectar
              </Button>
            </div>
          </div>
          {/* Indicación de dónde buscar mensajes */}
          <div className="mt-3 pt-3 border-t border-green-500/20">
            <p className="text-green-400/60 text-xs">
              Los mensajes de WhatsApp aparecen en <strong className="text-green-400/80">Chat → tab "Bot"</strong> (el bot responde automáticamente).
              Si el bot escala la conversación, pasa a <strong className="text-green-400/80">"Esperando"</strong>.
            </p>
          </div>

          {/* Registro en Cloud API — paso distinto de vincular el número a la WABA */}
          <div className="mt-3 pt-3 border-t border-green-500/20">
            <p className="text-gray-400 text-xs mb-2">
              Un número puede estar "vinculado" a la WABA pero NO "registrado" en la Cloud API — sin ese
              registro, Meta nunca entrega mensajes reales aunque todo lo demás se vea correcto.
              Si tu número ya tenía verificación en dos pasos activada en otra plataforma, indica su PIN de 6 dígitos aquí:
            </p>
            <div className="flex items-center gap-2">
              <input
                data-testid="wa-register-pin-input"
                type="text"
                maxLength={6}
                placeholder="PIN de 6 dígitos (opcional)"
                value={registerPin}
                onChange={e => setRegisterPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-200 w-56 focus:outline-none focus:border-amber-600"
              />
            </div>
            {registerResult && (
              <div data-testid="wa-register-result" className={`mt-2 rounded-lg p-3 border text-xs ${registerResult.registered ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                {registerResult.registered
                  ? 'Número registrado en la Cloud API. Los mensajes reales deberían empezar a llegar.'
                  : (registerResult.error || 'No se pudo registrar el número')}
              </div>
            )}
          </div>

          {/* Resultado verificado de la re-suscripción */}
          {resubResult && (
            <div data-testid="wa-resub-result" className={`mt-3 rounded-lg p-3 border text-xs ${resubResult.persisted ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <p className={resubResult.persisted ? 'text-green-300' : 'text-red-300'}>
                {resubResult.error || resubResult.message}
              </p>
              {resubResult.persisted === false && (
                <ol className="text-red-300/70 mt-2 space-y-1 list-decimal list-inside">
                  <li>Entra en business.facebook.com → WhatsApp Manager y acepta los Términos de WhatsApp Business si aparece el aviso.</li>
                  <li>Vuelve aquí y pulsa de nuevo "Re-suscribir".</li>
                  <li>Comprueba en el panel de abajo que la app aparece como suscrita.</li>
                </ol>
              )}
              {Array.isArray(resubResult.subscribed_apps_after) && resubResult.subscribed_apps_after.length > 0 && (
                <p className="text-gray-400 mt-1 font-mono break-all">apps: {resubResult.subscribed_apps_after.map(a => a?.whatsapp_business_api_data?.name || a?.name || a?.whatsapp_business_api_data?.id || '?').join(', ')}</p>
              )}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs font-medium">Diagnóstico: webhooks recibidos de Meta</p>
              <button
                data-testid="wa-load-log-btn"
                onClick={loadWebhookLog}
                disabled={loadingLog}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                {loadingLog ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                Ver log
              </button>
            </div>
            {webhookLog === null && (
              <p className="text-gray-600 text-xs">Haz clic en "Ver log", envía un WhatsApp desde tu móvil y vuelve a pulsar "Ver log" para ver si Meta está enviando el webhook.</p>
            )}
            {webhookLog !== null && webhookLog.length === 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-xs font-semibold">Sin webhooks recibidos de Meta</p>
                <p className="text-red-300/70 text-xs mt-1">Meta no está enviando peticiones a este servidor. Posibles causas: la suscripción de la WABA no está activa o hay un proxy bloqueando los POSTs de Meta. Prueba a hacer clic en "Re-suscribir".</p>
              </div>
            )}
            {webhookLog !== null && webhookLog.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-green-400 text-xs font-semibold">{webhookLog.filter(l => l.source !== 'sniffer').length} webhook(s) procesado(s) · {webhookLog.filter(l => l.source === 'sniffer').length} petición(es) detectada(s) por el sniffer</p>
                {webhookLog.slice(0, 12).map((log, i) => (
                  <div key={i} data-testid={`wa-log-row-${i}`} className="bg-gray-800 rounded-lg p-2 text-xs font-mono text-gray-400 break-all">
                    <span className="text-gray-300">{String(log.received_at || '').slice(0, 19).replace('T',' ')}</span>
                    {log.source === 'sniffer' ? (
                      <>
                        <span className={`ml-2 ${log.status_code >= 400 || log.status_code === 307 || log.status_code === 301 ? 'text-red-400' : 'text-yellow-400'}`}>{log.method} {log.path} → {log.status_code}</span>
                        <span className="ml-2 text-gray-500">{log.user_agent}</span>
                      </>
                    ) : (
                      <>
                        {log.phone_number_id && <span className="ml-2 text-indigo-400">pid:{String(log.phone_number_id).slice(-6)}</span>}
                        <span className="ml-2">{log.entries} entr.</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Panel diagnóstico: suscripción WABA */}
          <div className="mt-3 pt-3 border-t border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs font-medium">Diagnóstico: suscripción WABA en Meta</p>
              <button
                data-testid="wa-check-sub-btn"
                onClick={checkSubscription}
                disabled={loadingSub}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                {loadingSub ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                Comprobar
              </button>
            </div>
            {subInfo === null && (
              <p className="text-gray-600 text-xs">Comprueba si tu app de Meta está realmente suscrita a la WABA.</p>
            )}
            {subInfo?.error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-xs">{subInfo.error}</p>
              </div>
            )}
            {subInfo && !subInfo.error && (
              <div className="space-y-2">
                {/* Número exacto configurado en Sera */}
                <div data-testid="wa-configured-number" className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-1">Escribe EXACTAMENTE a este número (es el que corresponde al phone_number_id guardado en Sera):</p>
                  <p className="text-indigo-200 text-lg font-mono font-semibold">
                    {subInfo.configured_number ? `+${String(subInfo.configured_number).replace(/^\+/, '')}` : '— no disponible —'}
                  </p>
                  {subInfo.meta_phone_details?.verified_name && (
                    <p className="text-gray-500 text-xs mt-1">Nombre verificado: {subInfo.meta_phone_details.verified_name}</p>
                  )}
                </div>

                {/* Todos los números de la WABA */}
                {subInfo.waba_numbers?.length > 0 && (
                  <div data-testid="wa-waba-numbers" className="bg-gray-800 rounded-lg p-2 text-xs">
                    <p className="text-gray-500 mb-1">Números en esta WABA ({subInfo.waba_numbers.length}):</p>
                    {subInfo.waba_numbers.map((n, i) => (
                      <div key={i} className={`font-mono ${n.is_configured_in_sera ? 'text-green-400' : 'text-gray-500'}`}>
                        {n.is_configured_in_sera ? '✓ ' : '  '}+{String(n.display_phone_number).replace(/^\+/, '')} · {n.phone_number_id} · {n.status}
                        {n.is_configured_in_sera ? ' ← configurado en Sera' : ' (NO configurado en Sera)'}
                      </div>
                    ))}
                    {subInfo.waba_numbers.length > 1 && (
                      <p className="text-yellow-400 mt-1">Hay más de un número. Si escribes a uno que no sea el marcado con ✓, Sera no recibirá el mensaje.</p>
                    )}
                  </div>
                )}

                {/* App del token vs app suscrita */}
                {subInfo.token_app && (
                  <div data-testid="wa-token-app" className="bg-gray-800 rounded-lg p-2 text-xs font-mono space-y-0.5">
                    <div><span className="text-gray-500">App del token: </span><span className="text-gray-200">{subInfo.token_app.app_name || '?'} ({subInfo.token_app.app_id || '?'})</span></div>
                    <div><span className="text-gray-500">Token válido: </span><span className={subInfo.token_app.is_valid ? 'text-green-400' : 'text-red-400'}>{String(subInfo.token_app.is_valid)}</span></div>
                    <div><span className="text-gray-500">Token expira: </span><span className="text-gray-200">{subInfo.token_app.expires_at ? new Date(subInfo.token_app.expires_at * 1000).toLocaleString() : 'nunca (permanente)'}</span></div>
                    {subInfo.token_app.matches_subscribed_app ? (
                      <div className="text-green-400 whitespace-normal">✓ El token pertenece a la misma app suscrita a la WABA.</div>
                    ) : (
                      <div className="text-red-400 whitespace-normal">✗ El token pertenece a la app {subInfo.token_app.app_id || '?'} pero la WABA está suscrita a {subInfo.token_app.subscribed_app_ids?.join(', ') || 'otra app'}. El webhook debe configurarse en la app SUSCRITA, no en la del token.</div>
                    )}
                  </div>
                )}
                <div className="bg-gray-800 rounded-lg p-2 text-xs font-mono text-gray-400 space-y-0.5">
                  <div><span className="text-gray-500">WABA ID: </span><span className="text-gray-200">{subInfo.stored_config?.waba_id}</span></div>
                  {subInfo.waba_info && !subInfo.waba_info.error && (
                    <>
                      <div><span className="text-gray-500">WABA: </span><span className="text-gray-200">{subInfo.waba_info.name || '?'} · {subInfo.waba_info.country || '?'}</span></div>
                      <div data-testid="wa-account-review"><span className="text-gray-500">Revisión de cuenta: </span><span className={subInfo.waba_info.account_review_status === 'APPROVED' ? 'text-green-400' : 'text-yellow-400'}>{subInfo.waba_info.account_review_status || '?'}</span></div>
                    </>
                  )}
                  <div><span className="text-gray-500">Suscripción activa: </span><span className={subInfo.is_subscribed ? 'text-green-400' : 'text-red-400'}>{subInfo.is_subscribed ? 'SÍ' : 'NO'}</span></div>
                  <div><span className="text-gray-500">Phone Number ID: </span><span className="text-gray-200">{subInfo.stored_config?.phone_number_id}</span></div>
                  <div><span className="text-gray-500">Estado número: </span><span className={subInfo.meta_phone_details?.status === 'CONNECTED' ? 'text-green-400' : 'text-yellow-400'}>{subInfo.meta_phone_details?.status || '?'}</span></div>
                  <div data-testid="wa-cloud-api-registered"><span className="text-gray-500">Registrado en Cloud API: </span>
                    <span className={subInfo.cloud_api_registration?.registered ? 'text-green-400' : 'text-red-400'}>
                      {subInfo.cloud_api_registration?.registered ? 'SÍ' : (subInfo.cloud_api_registration?.registered === false ? 'NO — pulsa "Registrar número"' : 'Desconocido — pulsa "Registrar número"')}
                    </span>
                  </div>
                </div>
                {subInfo.cloud_api_registration && !subInfo.cloud_api_registration.registered && (
                  <div className="bg-red-950/50 border border-red-800 rounded-lg p-3 text-xs text-red-300">
                    "Vinculado" a la WABA y "registrado" en la Cloud API son pasos distintos en Meta. Sin el
                    registro, los mensajes reales nunca llegan aunque todo lo demás se vea correcto.
                    {subInfo.cloud_api_registration.register_error && (
                      <div className="mt-1 text-red-400">{subInfo.cloud_api_registration.register_error}</div>
                    )}
                  </div>
                )}
                {subInfo.webhook_override?.has_override && (
                  <div data-testid="wa-webhook-override-warning" className="bg-red-950/50 border border-red-800 rounded-lg p-3 text-xs text-red-300">
                    {subInfo.webhook_override.warning}
                    {subInfo.webhook_override.phone_override_uri && (
                      <div className="mt-1 text-red-400">URL alternativa (número): {subInfo.webhook_override.phone_override_uri}</div>
                    )}
                    {subInfo.webhook_override.waba_override_uri && (
                      <div className="mt-1 text-red-400">URL alternativa (WABA): {subInfo.webhook_override.waba_override_uri}</div>
                    )}
                  </div>
                )}
                <div className="bg-gray-800 rounded-lg p-2 text-xs">
                  <p className="text-gray-500 mb-1">Apps suscritas a la WABA:</p>
                  {subInfo.meta_subscribed_apps?.data?.length > 0 ? (
                    subInfo.meta_subscribed_apps.data.map((app, i) => (
                      <div key={i} className="text-green-400">{app.name || app.id || JSON.stringify(app)}</div>
                    ))
                  ) : (
                    <div className="text-red-400">Ninguna app suscrita — la WABA no tiene webhooks activos. Pulsa "Re-suscribir".</div>
                  )}
                  {subInfo.meta_subscribed_apps?.error && (
                    <div className="text-red-400">{JSON.stringify(subInfo.meta_subscribed_apps.error)}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Panel diagnóstico: URL de callback real en la app de Meta */}
          <div className="mt-3 pt-3 border-t border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs font-medium">Diagnóstico: URL de webhook real en la app de Meta</p>
              <button
                data-testid="wa-check-app-webhook-btn"
                onClick={checkAppWebhook}
                disabled={loadingAppWh}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                {loadingAppWh ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                Comprobar
              </button>
            </div>
            {appWh === null && (
              <p className="text-gray-600 text-xs">Pregunta a Meta a qué URL está enviando realmente los webhooks de WhatsApp y qué campos están suscritos.</p>
            )}
            {appWh?.error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-xs" data-testid="wa-app-webhook-error">{appWh.error}</p>
              </div>
            )}
            {appWh && !appWh.error && (
              <div data-testid="wa-app-webhook-info" className="bg-gray-800 rounded-lg p-2 text-xs font-mono space-y-1">
                <div><span className="text-gray-500">App ID del servidor: </span><span className="text-gray-200">{appWh.server_app_id}</span></div>
                <div className="whitespace-normal break-all"><span className="text-gray-500">Callback URL en Meta: </span><span className={appWh.callback_url ? 'text-gray-200' : 'text-red-400'}>{appWh.callback_url || '— ninguna —'}</span></div>
                <div><span className="text-gray-500">Campo "messages": </span><span className={appWh.messages_field_subscribed ? 'text-green-400' : 'text-red-400'}>{appWh.messages_field_subscribed ? 'suscrito' : 'NO suscrito'}</span></div>
                <div><span className="text-gray-500">Campos suscritos: </span><span className="text-gray-200">{(appWh.subscribed_fields || []).join(', ') || '—'}</span></div>
                <div><span className="text-gray-500">Activa: </span><span className={appWh.active ? 'text-green-400' : 'text-red-400'}>{String(appWh.active)}</span></div>
                {!appWh.whatsapp_subscription && (
                  <div className="text-red-400 whitespace-normal">✗ Esta app NO tiene ninguna suscripción al objeto whatsapp_business_account. Los webhooks se están configurando en otra app distinta a la del servidor.</div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* ── Opción A: Botón Facebook Login (recomendado para clientes) ── */}
          {metaAppId && (
            <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#25D366]" />
                <p className="text-gray-200 text-sm font-semibold">Conexión con un clic</p>
                <span className="text-[10px] bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full">Recomendado</span>
              </div>
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                Inicia sesión con tu cuenta de Facebook y autoriza el acceso a tu WhatsApp Business.
                Sera detecta tu número automáticamente. No necesitas crear ninguna app.
              </p>
              <button
                data-testid="wa-fb-login-btn"
                onClick={launchOAuth}
                disabled={connecting}
                className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166fe5] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm py-3 rounded-xl transition-all duration-200 shadow-lg shadow-blue-900/20">
                {connecting ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                )}
                {connecting ? 'Conectando...' : 'Continuar con Facebook'}
              </button>
              {!sdkReady && metaAppId && (
                <p className="text-gray-600 text-[11px] mt-2 text-center">Cargando SDK de Meta...</p>
              )}
            </div>
          )}

          {/* ── Separador ── */}
          {metaAppId && (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-800" />
              <span className="text-gray-600 text-xs">o</span>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
          )}

          {/* ── Opción B: Token de Sistema ── */}
          <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-gray-500" />
              <p className="text-gray-300 text-sm font-medium">Conexión por token de sistema</p>
            </div>
            <p className="text-gray-500 text-xs mb-4 leading-relaxed">
              Para conectar con un token de Usuario del Sistema de Meta Business.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">
                  Token de acceso
                  <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer"
                    className="ml-2 text-indigo-400 hover:text-indigo-300 font-normal">→ Meta Business Settings</a>
                </label>
                <textarea
                  data-testid="wa-quick-token-input"
                  value={quickToken}
                  onChange={e => setQuickToken(e.target.value)}
                  rows={2}
                  placeholder="EAAGm0PX4ZCps..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 font-mono resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
                />
              </div>
              <div>
                <button type="button" onClick={() => setShowWabaHelp(v => !v)}
                  className="text-[11px] text-gray-500 hover:text-gray-400 underline">
                  {showWabaHelp ? '▲ Ocultar' : '▼ Añadir WABA ID (si la detección automática falla)'}
                </button>
                {showWabaHelp && (
                  <div className="mt-2 space-y-1.5">
                    <input
                      data-testid="wa-waba-id-input"
                      value={quickWabaId}
                      onChange={e => setQuickWabaId(e.target.value)}
                      placeholder="WABA ID (ej: 123456789012345)"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 font-mono focus:outline-none focus:border-indigo-500"
                    />
                    <p className="text-gray-600 text-[11px]">
                      Meta Business Manager →
                      <a href="https://business.facebook.com/settings/whatsapp-business-accounts" target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-400 hover:text-indigo-300">
                        Cuentas de WhatsApp Business
                      </a>
                      → ID numérico de tu cuenta
                    </p>
                  </div>
                )}
              </div>
              <button
                data-testid="wa-connect-btn"
                onClick={detectToken}
                disabled={connecting || !quickToken.trim()}
                className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 rounded-xl transition-all">
                {connecting ? <RefreshCw size={14} className="animate-spin" /> : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                )}
                {connecting ? 'Detectando...' : 'Detectar número'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Selector de número (multi-número) ───────────────── */}
      {pendingPhones && (
        <div className="bg-gray-900 border border-indigo-500/40 rounded-xl p-5">
          <div className="flex items-start gap-2 mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-amber-300 text-xs leading-relaxed">
              <strong>Importante:</strong> al vincular, este número dejará de funcionar como WhatsApp normal (app) y solo recibirá mensajes a través de la API. Asegúrate de usar un número dedicado.
            </p>
          </div>
          <p className="text-gray-200 text-sm font-semibold mb-3">Selecciona el número a conectar</p>
          <div className="space-y-2">
            {pendingPhones.map((phone) => (
              <button
                key={phone.id}
                data-testid={`wa-select-phone-${phone.id}`}
                onClick={() => selectPhone(phone)}
                disabled={saving}
                className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-indigo-500/50 rounded-lg px-4 py-3 text-left transition-colors">
                <div>
                  <p className="text-white text-sm font-medium">{phone.verified_name || phone.display_phone_number}</p>
                  <p className="text-gray-400 text-xs">{phone.display_phone_number}</p>
                  {phone.waba_name && (
                    <p className="text-indigo-400/70 text-[11px] mt-0.5">WABA: {phone.waba_name} · {phone.waba_id}</p>
                  )}
                </div>
                <span className="text-indigo-400 text-xs">Seleccionar →</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setPendingPhones(null); setPendingToken(''); }}
            className="mt-3 text-xs text-gray-500 hover:text-gray-400">
            Cancelar
          </button>
        </div>
      )}

      {/* ── Confirmación antes de vincular (1 número) ─────────── */}
      {preview && !pendingPhones && (
        <div className="bg-gray-900 border border-amber-500/40 rounded-xl p-5 space-y-4">
          {/* Número detectado */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#25D366]/15 flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div>
              <p className="text-gray-100 text-sm font-semibold">{preview.phone_name || 'Número detectado'}</p>
              <p className="text-gray-400 text-xs font-mono">{preview.phone_number}</p>
              <p className="text-gray-600 text-[11px]">WABA ID: {preview.waba_id}</p>
            </div>
          </div>

          {/* Aviso importante */}
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-amber-300 text-xs font-semibold">Lee esto antes de continuar</p>
              <p className="text-amber-300/80 text-xs leading-relaxed">
                Al vincular este número a la API de WhatsApp Business:
              </p>
              <ul className="text-amber-300/70 text-xs space-y-0.5 ml-3 list-disc">
                <li>Este número <strong>ya no funcionará</strong> en la app de WhatsApp ni WhatsApp Business</li>
                <li>Los chats existentes en la app <strong>se perderán</strong></li>
                <li>Solo recibirá mensajes a través de Sera (API)</li>
              </ul>
              <p className="text-amber-400/60 text-[11px] mt-1">
                Usa un número dedicado exclusivamente para este canal. Si necesitas conservar el uso normal, cancela ahora.
              </p>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3">
            <button
              data-testid="wa-confirm-connect-btn"
              onClick={confirmConnect}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5a] disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-xl transition-all">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle size={13} />}
              {saving ? 'Conectando...' : 'Sí, vincular este número'}
            </button>
            <button
              onClick={() => { setPreview(null); }}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 font-medium text-sm py-2.5 rounded-xl transition-all">
              <X size={13} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Info del webhook (siempre visible) ──────────────── */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
        <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Datos del Webhook</p>
        <div>
          <label className="block text-gray-500 text-xs mb-1">URL del Webhook</label>
          <div className="flex items-center gap-2">
            <code data-testid="wa-webhook-url"
              className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-xs text-green-400 font-mono truncate">
              {webhookUrl || 'Cargando...'}
            </code>
            <button onClick={() => copyToClipboard(webhookUrl)}
              className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-400 hover:text-white text-xs transition-colors flex-shrink-0">
              Copiar
            </button>
          </div>
        </div>
        <div>
          <label className="block text-gray-500 text-xs mb-1">Verify Token</label>
          <div className="flex items-center gap-2">
            <code data-testid="wa-verify-token"
              className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-xs text-amber-400 font-mono">
              {verifyToken || 'Cargando...'}
            </code>
            <button onClick={() => copyToClipboard(verifyToken)}
              className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-400 hover:text-white text-xs transition-colors flex-shrink-0">
              Copiar
            </button>
          </div>
        </div>
        <p className="text-gray-600 text-xs">
          Meta App Dashboard → WhatsApp → Configuración → Webhook
        </p>
      </div>

      {/* ── Configuración manual (colapsable) ───────────────── */}
      <div className="border border-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowManual(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-900 hover:bg-gray-800/80 transition-colors">
          <span className="text-gray-400 text-xs font-medium">Configuración manual avanzada</span>
          <span className="text-gray-600 text-xs">{showManual ? '▲ Ocultar' : '▼ Mostrar'}</span>
        </button>
        {showManual && (
          <div className="p-4 bg-gray-900/50 space-y-4">
            <p className="text-gray-500 text-xs">
              Usa esto solo si el flujo automático no funciona, o si tienes un token de System User permanente.
            </p>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">
                Phone Number ID <span className="text-red-400">*</span>
              </label>
              <input data-testid="wa-phone-number-id-input"
                value={manualPhoneId} onChange={e => setManualPhoneId(e.target.value)}
                placeholder="Ej: 123456789012345"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">Número (opcional)</label>
              <input data-testid="wa-phone-number-input"
                value={manualPhone} onChange={e => setManualPhone(e.target.value)}
                placeholder="+34600123456"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">
                Access Token <span className="text-red-400">*</span>
              </label>
              {configured && !manualToken && (
                <p className="text-gray-500 text-xs mb-1">Token guardado: <span className="font-mono">{tokenHint}</span></p>
              )}
              <textarea data-testid="wa-access-token-input"
                value={manualToken} onChange={e => setManualToken(e.target.value)}
                rows={2}
                placeholder={configured ? 'Deja vacío para conservar el token actual...' : 'EAAGm0PX4ZCps...'}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 font-mono resize-none focus:outline-none focus:border-indigo-500" />
            </div>
            <Button data-testid="wa-save-btn" onClick={saveManual}
              disabled={saving || !manualPhoneId.trim() || (!configured && !manualToken.trim())}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
              {configured ? 'Actualizar' : 'Guardar configuración'}
            </Button>
          </div>
        )}
      </div>

    </div>
  );
}


// ── SatisfactionTab ──────────────────────────────────────────────────────────
function SatisfactionTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/chat/settings').then(r => setSettings(r.data.settings)).catch(() => {});
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/api/chat/settings', settings);
      setSettings(r.data.settings);
      toast.success('Configuración de satisfacción guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  if (!settings) return <div className="text-gray-500 text-sm py-8 text-center">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="satisfaction-tab">
      {/* Web */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">Chat Web</p>
            <p className="text-gray-400 text-xs mt-0.5">Formulario flotante de estrellas al cerrar la conversación</p>
          </div>
          <Switch
            data-testid="satisfaction-web-toggle"
            checked={!!settings.satisfaction_enabled_web}
            onCheckedChange={v => upd('satisfaction_enabled_web', v)}
          />
        </div>
        {settings.satisfaction_enabled_web && (
          <div className="space-y-3 pl-1 border-l-2 border-indigo-500/30">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Momento de aparición</label>
              <select
                value={settings.satisfaction_trigger || 'on_close'}
                onChange={e => upd('satisfaction_trigger', e.target.value)}
                className="bg-gray-900 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 w-full"
                data-testid="satisfaction-trigger-select"
              >
                <option value="on_close">Al cerrar la conversación</option>
                <option value="inactivity">Tras X minutos de inactividad</option>
              </select>
            </div>
            {settings.satisfaction_trigger === 'inactivity' && (
              <div>
                <label className="text-gray-400 text-xs block mb-1">Minutos de inactividad</label>
                <input
                  type="number" min="5" max="1440"
                  value={settings.satisfaction_trigger_minutes || 60}
                  onChange={e => upd('satisfaction_trigger_minutes', parseInt(e.target.value) || 60)}
                  className="bg-gray-900 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 w-full"
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-300 text-xs">Permitir activar manualmente</p>
                <p className="text-gray-500 text-xs">El agente puede disparar la encuesta desde el inbox</p>
              </div>
              <Switch
                data-testid="satisfaction-manual-toggle"
                checked={!!settings.satisfaction_allow_manual}
                onCheckedChange={v => upd('satisfaction_allow_manual', v)}
              />
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">WhatsApp</p>
            <p className="text-gray-400 text-xs mt-0.5">Mensaje automático pidiendo valoración del 1 al 5</p>
          </div>
          <Switch
            data-testid="satisfaction-wa-toggle"
            checked={!!settings.satisfaction_enabled_whatsapp}
            onCheckedChange={v => upd('satisfaction_enabled_whatsapp', v)}
          />
        </div>
        {settings.satisfaction_enabled_whatsapp && (
          <div className="space-y-2 pl-1 border-l-2 border-green-500/30">
            <label className="text-gray-400 text-xs block">Texto del mensaje de valoración</label>
            <textarea
              value={settings.satisfaction_message_whatsapp || ''}
              onChange={e => upd('satisfaction_message_whatsapp', e.target.value)}
              rows={3}
              className="w-full bg-gray-900 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 resize-none"
              placeholder="¿Cómo valorarías la atención recibida?..."
              data-testid="satisfaction-wa-message"
            />
            <p className="text-gray-600 text-xs">Timeout: 30 minutos. Si el cliente no responde, se guarda como "sin valoración".</p>
          </div>
        )}
      </div>

      <Button onClick={save} disabled={saving} data-testid="satisfaction-save-btn"
        className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
        {saving ? 'Guardando...' : 'Guardar configuración'}
      </Button>
    </div>
  );
}


// ── AutoCloseTab ─────────────────────────────────────────────────────────────
function AutoCloseTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/chat/settings').then(r => setSettings(r.data.settings)).catch(() => {});
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/api/chat/settings', settings);
      setSettings(r.data.settings);
      toast.success('Configuración de conversaciones guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  if (!settings) return <div className="text-gray-500 text-sm py-8 text-center">Cargando...</div>;

  const CHANNELS = [
    { key: 'web', label: 'Chat Web', defaultHours: 24, color: 'indigo' },
    { key: 'whatsapp', label: 'WhatsApp', defaultHours: 24, color: 'green' },
    { key: 'email', label: 'Email', defaultHours: 72, color: 'amber' },
  ];

  return (
    <div className="space-y-5" data-testid="auto-close-tab">
      <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
        <p className="text-amber-300 text-xs font-medium">Regla de cierre automático</p>
        <p className="text-amber-200/70 text-xs mt-0.5">Solo se cierra si el <strong>último mensaje fue del agente o la IA</strong>. Si el cliente escribió último y el agente no ha respondido, la conversación permanece abierta.</p>
      </div>

      {CHANNELS.map(ch => (
        <div key={ch.key} className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-white font-medium text-sm">{ch.label}</p>
            <Switch
              data-testid={`auto-close-${ch.key}-toggle`}
              checked={!!settings[`auto_close_enabled_${ch.key}`]}
              onCheckedChange={v => upd(`auto_close_enabled_${ch.key}`, v)}
            />
          </div>
          {settings[`auto_close_enabled_${ch.key}`] && (
            <div className="space-y-3 pl-1 border-l-2 border-gray-600/50">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Horas sin respuesta del cliente</label>
                <input
                  type="number" min="1" max="720"
                  value={settings[`auto_close_hours_${ch.key}`] || ch.defaultHours}
                  onChange={e => upd(`auto_close_hours_${ch.key}`, parseInt(e.target.value) || ch.defaultHours)}
                  className="bg-gray-900 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 w-32"
                  data-testid={`auto-close-hours-${ch.key}`}
                />
                <span className="text-gray-500 text-xs ml-2">horas (por defecto: {ch.defaultHours}h)</span>
              </div>

              {/* Mensaje de cierre — NO aplica a Email (no se envía nada al cliente) */}
              {ch.key === 'email' ? (
                <p className="text-gray-400 text-xs leading-relaxed bg-gray-900/40 rounded-lg px-3 py-2 border border-gray-700/40" data-testid="auto-close-email-help">
                  Se aplica a <strong className="text-gray-300">Email de Soporte</strong> y <strong className="text-gray-300">Mensajes de pedidos</strong>. Cierra los hilos en "Respondido" si el cliente no vuelve a escribir en este plazo. No se envía ningún mensaje al cliente.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-gray-300 text-xs">Enviar mensaje de cierre al cliente</p>
                    <Switch
                      data-testid={`close-msg-enabled-${ch.key}`}
                      checked={!!settings[`auto_close_message_enabled_${ch.key}`]}
                      onCheckedChange={v => upd(`auto_close_message_enabled_${ch.key}`, v)}
                    />
                  </div>
                  {settings[`auto_close_message_enabled_${ch.key}`] && (
                    <textarea
                      value={settings[`auto_close_message_${ch.key}`] || ''}
                      onChange={e => upd(`auto_close_message_${ch.key}`, e.target.value)}
                      rows={2}
                      className="w-full bg-gray-900 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 resize-none"
                      placeholder="Esta conversación se ha cerrado por inactividad..."
                      data-testid={`close-msg-text-${ch.key}`}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}

      <Button onClick={save} disabled={saving} data-testid="auto-close-save-btn"
        className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
        {saving ? 'Guardando...' : 'Guardar configuración'}
      </Button>
    </div>
  );
}


export default function ChatAIPage() {
  const [tab, setTab] = useState('faqs');
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({});
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Chat module org-level toggle (independent from search AI)
  const [orgChatEnabled, setOrgChatEnabled] = useState(true);
  const [togglingChat, setTogglingChat] = useState(false);

  // Test chat state
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  // Personality state
  const [name, setName] = useState('');
  const [tone, setTone] = useState('friendly');
  const [lang, setLang] = useState('es');
  const [forbidden, setForbidden] = useState('');
  const [alwaysEsc, setAlwaysEsc] = useState('');
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/chat/knowledge');
      setItems(res.data.items || []);
      const cfg = res.data.config || {};
      setConfig(cfg);
      setName(cfg.assistant_name || 'Sera');
      setTone(cfg.tone || 'friendly');
      setLang(cfg.default_language || 'es');
      setForbidden((cfg.forbidden_topics || []).join(', '));
      setAlwaysEsc((cfg.always_escalate_topics || []).join(', '));
      setWelcomeMsg(cfg.chat_welcome_message || '');
      setCustomInstructions(cfg.custom_instructions || '');
      // Load org-level chat toggle
      setOrgChatEnabled(res.data.org_chat_enabled !== false);
      // Load triggers in parallel (best effort — endpoint exists only if backend is up to date)
      try {
        const tr = await api.get('/api/chat/triggers');
        setTriggers(tr.data.items || []);
      } catch { /* triggers endpoint may not exist on older backends */ }
    } catch {
      toast.error('Error al cargar la configuración');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleChat = async (enabled) => {
    setTogglingChat(true);
    try {
      await api.put('/api/chat/toggle', { enabled });
      setOrgChatEnabled(enabled);
      toast.success(enabled ? 'Chat activado' : 'Chat desactivado');
    } catch {
      toast.error('Error al cambiar el estado del chat');
    } finally {
      setTogglingChat(false);
    }
  };

  const saveItem = async (item) => {
    try {
      if (item.item_id) {
        await api.put(`/api/chat/knowledge/${item.item_id}`, item);
        setItems(prev => prev.map(i => i.item_id === item.item_id ? item : i));
      } else {
        const res = await api.post('/api/chat/knowledge', item);
        setItems(prev => [...prev.filter(i => i.item_id), res.data.item]);
      }
      toast.success('Guardado');
    } catch { toast.error('Error al guardar'); }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/api/chat/knowledge/${itemId}`);
      setItems(prev => prev.filter(i => i.item_id !== itemId));
      toast.success('Eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  const addNew = () => {
    setItems(prev => [{ question: '', answer: '', type: 'faq', active: true }, ...prev]);
  };

  const savePersonality = async () => {
    setSaving(true);
    try {
      await api.put('/api/chat/config', {
        assistant_name: name,
        tone,
        default_language: lang,
        forbidden_topics: forbidden.split(',').map(s => s.trim()).filter(Boolean),
        always_escalate_topics: alwaysEsc.split(',').map(s => s.trim()).filter(Boolean),
        chat_welcome_message: welcomeMsg.trim() || null,
        custom_instructions: customInstructions.slice(0, 2000),
      });
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const addPolicy = () => {
    setItems(prev => [{ question: null, answer: '', type: 'policy', active: true }, ...prev]);
  };

  // ── Triggers CRUD ─────────────────────────────────────────
  const addTrigger = () => {
    setTriggers(prev => [{
      // sentinel - no trigger_id means "new, not saved yet"
      name: 'Nuevo trigger',
      active: true,
      condition: { type: 'time_on_product_page', seconds: 30 },
      display: { format: 'bubble_tooltip', message: '¿Tienes dudas sobre {product_name}?', show_once_per_session: true },
    }, ...prev]);
  };

  const saveTrigger = async (idx, body) => {
    try {
      if (body.trigger_id) {
        await api.put(`/api/chat/triggers/${body.trigger_id}`, body);
        toast.success('Trigger actualizado');
      } else {
        const r = await api.post('/api/chat/triggers', body);
        setTriggers(prev => prev.map((t, i) => i === idx ? r.data.item : t));
        toast.success('Trigger creado');
        return;
      }
      setTriggers(prev => prev.map((t, i) => i === idx ? { ...t, ...body } : t));
    } catch (e) {
      toast.error('Error guardando trigger: ' + (e?.response?.data?.detail || ''));
    }
  };

  const deleteTrigger = async (idx, trigger) => {
    if (!trigger.trigger_id) {
      setTriggers(prev => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!window.confirm('¿Eliminar este trigger?')) return;
    try {
      await api.delete(`/api/chat/triggers/${trigger.trigger_id}`);
      setTriggers(prev => prev.filter((_, i) => i !== idx));
      toast.success('Trigger eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  const toggleTrigger = async (idx, trigger) => {
    if (!trigger.trigger_id) return;
    const newActive = !trigger.active;
    try {
      await api.put(`/api/chat/triggers/${trigger.trigger_id}`, { ...trigger, active: newActive });
      setTriggers(prev => prev.map((t, i) => i === idx ? { ...t, active: newActive } : t));
    } catch { toast.error('Error al cambiar estado'); }
  };

  const sendTestMessage = async () => {
    if (!testInput.trim()) return;
    const userMsg = { role: 'user', content: testInput };
    setTestMessages(prev => [...prev, userMsg]);
    const input = testInput;
    setTestInput('');
    setTestLoading(true);
    try {
      const res = await api.post('/api/chat/knowledge/test', { message: input });
      setTestMessages(prev => [...prev, {
        role: 'bot',
        content: res.data.reply,
        model_used: res.data.model_used,
        would_escalate: res.data.would_escalate,
      }]);
    } catch {
      setTestMessages(prev => [...prev, { role: 'bot', content: 'Error al obtener respuesta.' }]);
    } finally { setTestLoading(false); }
  };

  const faqs = items.filter(i => i.type === 'faq' || i.type === 'custom');
  const policies = items.filter(i => i.type === 'policy');

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <RefreshCw size={20} className="animate-spin text-indigo-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-white font-semibold text-lg flex items-center gap-2">
              <Bot size={18} className="text-indigo-400" />
              Entrenamiento Chat AI
            </h1>
            <p className="text-gray-400 text-xs mt-1">
              Configura el conocimiento y personalidad del asistente virtual
            </p>
          </div>

          {/* Org-level chat toggle — completely independent from search AI */}
          <div
            data-testid="chat-module-toggle-row"
            className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 flex-shrink-0"
          >
            {orgChatEnabled ? (
              <Bot size={15} className="text-indigo-400" />
            ) : (
              <PowerOff size={15} className="text-gray-500" />
            )}
            <div className="min-w-0">
              <p className="text-xs font-medium text-white leading-tight">
                {orgChatEnabled ? 'Chat activo' : 'Chat inactivo'}
              </p>
              <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                Independiente del AI del buscador
              </p>
            </div>
            <Switch
              data-testid="chat-module-toggle"
              checked={orgChatEnabled}
              onCheckedChange={handleToggleChat}
              disabled={togglingChat}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-800 mb-6">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`chat-ai-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors rounded-t-lg ${
                  tab === t.id
                    ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-950/30'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <Icon size={12} />{t.label}
              </button>
            );
          })}
        </div>

        {/* FAQs */}
        {tab === 'faqs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">{faqs.filter(i => i.item_id).length} preguntas frecuentes</p>
              <Button size="sm" onClick={addNew}
                data-testid="add-faq-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
                <Plus size={12} /> Añadir FAQ
              </Button>
            </div>
            {faqs.length === 0 && (
              <div className="text-center py-10 text-gray-600">
                <BookOpen size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sin FAQs configuradas aún</p>
              </div>
            )}
            {faqs.map((item, idx) => (
              <FAQItem key={item.item_id || `new-${idx}`} item={item}
                onSave={saveItem} onDelete={deleteItem} />
            ))}
          </div>
        )}

        {/* Policies */}
        {tab === 'policies' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">{policies.filter(i => i.item_id).length} políticas</p>
              <Button size="sm" onClick={addPolicy}
                data-testid="add-policy-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
                <Plus size={12} /> Añadir política
              </Button>
            </div>
            {policies.length === 0 && (
              <div className="text-center py-10 text-gray-600">
                <FileText size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sin políticas configuradas</p>
                <p className="text-xs mt-1">Ej: política de devoluciones, envíos, garantías...</p>
              </div>
            )}
            {policies.map((item, idx) => (
              <FAQItem key={item.item_id || `new-pol-${idx}`} item={item}
                onSave={saveItem} onDelete={deleteItem} />
            ))}
          </div>
        )}

        {/* Custom Instructions — comportamiento personalizado del asistente */}
        {tab === 'instructions' && (
          <div className="space-y-4">
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
              <p className="text-indigo-300 text-xs leading-relaxed">
                <strong>Instrucciones personalizadas con prioridad máxima.</strong> Estas reglas se inyectan
                al inicio del prompt de Claude en cada conversación. Úsalas para reglas específicas de tu
                negocio que el asistente debe seguir siempre.
              </p>
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">
                Reglas de comportamiento (texto libre)
              </label>
              <p className="text-gray-500 text-xs mb-2">
                Máximo 2000 caracteres · Una regla por línea recomendado
              </p>
              <textarea
                value={customInstructions}
                onChange={e => setCustomInstructions(e.target.value.slice(0, 2000))}
                data-testid="custom-instructions-input"
                rows={14}
                placeholder={`Ejemplos:
- Siempre que el cliente pregunte por un recambio técnico, pregunta el modelo de moto y el año si no lo ha indicado
- Si no encuentras el producto exacto, sugiere alternativas compatibles
- Ante cualquier reclamación, escala siempre a un agente humano
- Para pedidos especiales o personalizados, deriva al equipo comercial
- No confirmes plazos de envío exactos — di "entre 2 y 5 días hábiles" como rango`}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 resize-y focus:outline-none focus:border-indigo-500 font-mono leading-relaxed"
              />
              <div className="flex justify-between mt-1">
                <span className="text-gray-500 text-xs">
                  {customInstructions.length} / 2000 caracteres
                </span>
                {customInstructions.length >= 2000 && (
                  <span className="text-amber-400 text-xs">Límite alcanzado</span>
                )}
              </div>
            </div>
            <Button onClick={savePersonality} disabled={saving}
              data-testid="save-instructions-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
              Guardar instrucciones
            </Button>
          </div>
        )}

        {/* Triggers — Mensajes proactivos */}
        {tab === 'triggers' && (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-amber-200 text-xs leading-relaxed">
                <strong>Triggers proactivos.</strong> Mensajes que aparecen automáticamente según el
                comportamiento del visitante (sin necesidad de que abra el chat).
                Máximo 1 trigger por sesión global, y cada uno respeta "mostrar una vez".
              </p>
            </div>
            <Button onClick={addTrigger} data-testid="add-trigger-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              <Plus size={13} /> Nuevo trigger
            </Button>
            <div className="space-y-3">
              {triggers.length === 0 && (
                <div className="text-gray-500 text-sm py-6 text-center border border-dashed border-gray-800 rounded-lg">
                  Aún no tienes triggers configurados.
                </div>
              )}
              {triggers.map((t, i) => (
                <TriggerCard
                  key={t.trigger_id || `new-${i}`}
                  trigger={t} idx={i}
                  onSave={saveTrigger}
                  onDelete={deleteTrigger}
                  onToggle={toggleTrigger}
                />
              ))}
            </div>
          </div>
        )}

        {/* Personality */}
        {tab === 'personality' && (
          <div className="space-y-5">
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">Nombre del asistente</label>
              <input value={name} onChange={e => setName(e.target.value)}
                data-testid="assistant-name-input"
                placeholder="Sera"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-2">Tono</label>
              <div className="flex gap-3">
                {['friendly', 'formal'].map(t => (
                  <button key={t} onClick={() => setTone(t)}
                    className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                      tone === t ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}>
                    {t === 'friendly' ? 'Cercano y amigable' : 'Formal y profesional'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">Idioma por defecto</label>
              <select value={lang} onChange={e => setLang(e.target.value)}
                data-testid="default-language-select"
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500">
                {['es', 'fr', 'en', 'it', 'de', 'pt'].map(l => (
                  <option key={l} value={l}>{l.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">Mensaje de bienvenida del chat</label>
              <p className="text-gray-500 text-xs mb-2">Se mostrará la primera vez que un visitante abra el chat. Déjalo vacío para usar el saludo por defecto.</p>
              <textarea value={welcomeMsg} onChange={e => setWelcomeMsg(e.target.value)}
                data-testid="chat-welcome-message-input"
                rows={3} placeholder="¡Hola! ¿En qué puedo ayudarte con tu compra?"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500" />
            </div>
            <Button onClick={savePersonality} disabled={saving}
              data-testid="save-personality-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
              Guardar configuración
            </Button>
          </div>
        )}

        {/* Limits */}
        {tab === 'limits' && (
          <div className="space-y-5">
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">
                Temas que NO debe responder
              </label>
              <p className="text-gray-500 text-xs mb-2">Separados por coma (ej: política, religión, competencia)</p>
              <textarea value={forbidden} onChange={e => setForbidden(e.target.value)}
                data-testid="forbidden-topics-input"
                rows={3} placeholder="política, religión, temas no relacionados con la tienda..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-gray-300 text-xs font-medium mb-1">
                Escalar SIEMPRE a humano si el tema es...
              </label>
              <p className="text-gray-500 text-xs mb-2">Separados por coma (ej: reclamación legal, fraude, reembolso urgente)</p>
              <textarea value={alwaysEsc} onChange={e => setAlwaysEsc(e.target.value)}
                data-testid="always-escalate-input"
                rows={3} placeholder="reclamación legal, fraude, pedido extraviado..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-500" />
            </div>
            <Button onClick={savePersonality} disabled={saving}
              data-testid="save-limits-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
              Guardar límites
            </Button>
          </div>
        )}

        {/* Welcome Cards */}
        {tab === 'welcome' && <WelcomeCardsTab />}

        {/* Carriers */}
        {tab === 'carriers' && <CarriersTab />}

        {/* WhatsApp */}
        {tab === 'whatsapp' && <WhatsAppTab />}

        {/* Horario */}
        {tab === 'horario' && <BusinessHoursTab />}

        {/* Satisfacción */}
        {tab === 'satisfaction' && <SatisfactionTab />}

        {/* Auto-cierre */}
        {tab === 'auto-close' && <AutoCloseTab />}

        {/* Test en vivo */}
        {tab === 'test' && (
          <div>
            <p className="text-gray-400 text-xs mb-4">
              Prueba el asistente con la configuración actual antes de publicarlo.
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-xl h-96 overflow-y-auto p-4 mb-3">
              {testMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-600">
                  <div className="text-center">
                    <Bot size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-xs">Escribe un mensaje para probar el asistente</p>
                  </div>
                </div>
              ) : (
                testMessages.map((msg, i) => (
                  <div key={`${msg.role}-${i}`}>
                    <ChatBubble role={msg.role} content={msg.content} model_used={msg.model_used} />
                    {msg.would_escalate && (
                      <p className="text-amber-400 text-xs flex items-center gap-1 mb-2 ml-2">
                        <AlertTriangle size={11} /> Se escalaría a humano
                      </p>
                    )}
                  </div>
                ))
              )}
              {testLoading && (
                <div className="flex justify-start mb-3">
                  <div className="bg-gray-800 px-3 py-2 rounded-2xl text-gray-400 text-sm flex gap-1 items-center">
                    <RefreshCw size={12} className="animate-spin" /> Pensando...
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={testInput}
                onChange={e => setTestInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendTestMessage()}
                data-testid="test-message-input"
                placeholder="Escribe una pregunta de prueba..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              />
              <Button onClick={sendTestMessage} disabled={testLoading || !testInput.trim()}
                data-testid="send-test-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Send size={14} />
              </Button>
            </div>
            <div className="flex justify-end mt-2">
              <button onClick={() => setTestMessages([])}
                className="text-xs text-gray-500 hover:text-gray-300">
                Limpiar chat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
